/**
 * useSpaceFeed.js
 * Place at: src/hooks/useSpaceFeed.js
 *
 * Space feed with full caching + pagination.
 * Same architecture as FeedContext but scoped to a single space.
 *
 * Cache:
 *   TTL: 5 minutes (same as main feed)
 *   Key: "space|{spaceId}|{sort}"
 *   Invalidated on: new post, mod action, page unmount
 *
 * Optimizations:
 *   - AbortSignal on all fetches
 *   - loadingRef prevents duplicate fetches
 *   - Stale-while-revalidate: shows cache instantly, fetches in bg
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { fetchSpaceFeedPage, SPACE_PAGE_SIZE } from "../lib/spaceQueries";

const SPACE_FEED_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const spaceFeedCache = new Map(); // module-level cache shared across instances

function getCacheKey(spaceId, sort) {
  return `space|${spaceId}|${sort}`;
}

export function useSpaceFeed(spaceId, userId) {
  const [posts,        setPosts]        = useState([]);
  const [voteMap,      setVoteMap]      = useState({});
  const [bookmarkMap,  setBookmarkMap]  = useState({});
  const [reportMap,    setReportMap]    = useState({});
  const [loading,      setLoading]      = useState(false);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [hasMore,      setHasMore]      = useState(true);
  const [error,        setError]        = useState(null);
  const [sort,         setSort]         = useState("new");
  const [staleBanner,  setStaleBanner]  = useState(false);

  const pageRef      = useRef(0);
  const loadingRef   = useRef(false);
  const abortCtrlRef = useRef(null);

  // ── Fetch page ─────────────────────────────────────────────────────────────
  const fetchFeed = useCallback(async (reset = false, overrideSort) => {
    if (!spaceId || !userId) return;
    if (loadingRef.current && !reset) return;

    if (abortCtrlRef.current) abortCtrlRef.current.abort();
    const controller = new AbortController();
    abortCtrlRef.current = controller;
    const { signal } = controller;

    const activeSort = overrideSort ?? sort;

    // Check cache on reset
    if (reset) {
      const cacheKey = getCacheKey(spaceId, activeSort);
      const cached   = spaceFeedCache.get(cacheKey);
      if (cached && Date.now() - cached.fetchedAt < SPACE_FEED_CACHE_TTL) {
        loadingRef.current = false;
        setPosts(cached.posts);
        setVoteMap(cached.voteMap);
        setBookmarkMap(cached.bookmarkMap);
        setReportMap(cached.reportMap);
        setHasMore(cached.hasMore);
        pageRef.current = cached.pageRef;
        setStaleBanner(true);
        return;
      }
      setStaleBanner(false);
    }

    loadingRef.current = true;
    reset ? setLoading(true) : setLoadingMore(true);
    setError(null);

    const currentPage = reset ? 0 : pageRef.current;

    try {
      const { posts: fetched, voteMap: voteM, bookmarkMap: bookmarkM, reportMap: reportM } =
        await fetchSpaceFeedPage(spaceId, userId, {
          page:   currentPage,
          sort:   activeSort,
          signal,
        });

      if (signal.aborted) return;

      const more = fetched.length === SPACE_PAGE_SIZE;
      setHasMore(more);

      if (reset) {
        setPosts(fetched);
        setVoteMap(voteM);
        setBookmarkMap(bookmarkM);
        setReportMap(reportM);
        pageRef.current = 1;

        // Store in cache
        const cacheKey = getCacheKey(spaceId, activeSort);
        spaceFeedCache.set(cacheKey, {
          posts:       fetched,
          voteMap:     voteM,
          bookmarkMap: bookmarkM,
          reportMap:   reportM,
          hasMore:     more,
          pageRef:     1,
          fetchedAt:   Date.now(),
        });
      } else {
        setPosts(prev => [...prev, ...fetched]);
        setVoteMap(prev => ({ ...prev, ...voteM }));
        setBookmarkMap(prev => ({ ...prev, ...bookmarkM }));
        setReportMap(prev => ({ ...prev, ...reportM }));
        pageRef.current = currentPage + 1;
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("useSpaceFeed fetchFeed error:", err);
      setError("Failed to load posts. Please try again.");
    } finally {
      if (abortCtrlRef.current === controller) {
        loadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [spaceId, userId, sort]);

  // ── Initial load + sort change ─────────────────────────────────────────────
  useEffect(() => {
    if (spaceId && userId) {
      setPosts([]);
      pageRef.current = 0;
      fetchFeed(true);
    }
    return () => {
      if (abortCtrlRef.current) abortCtrlRef.current.abort();
    };
  }, [spaceId, userId, sort]);

  // ── Load more ──────────────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchFeed(false);
  }, [fetchFeed, hasMore]);

  // ── Sort change ────────────────────────────────────────────────────────────
  const changeSort = useCallback((newSort) => {
    setSort(newSort);
    setPosts([]);
    pageRef.current = 0;
  }, []);

  // ── Invalidate cache (after new post or mod action) ────────────────────────
  const invalidateCache = useCallback(() => {
    const cacheKey = getCacheKey(spaceId, sort);
    spaceFeedCache.delete(cacheKey);
    setStaleBanner(false);
    setPosts([]);
    pageRef.current = 0;
    fetchFeed(true);
  }, [spaceId, sort, fetchFeed]);

  // ── Optimistic post add ────────────────────────────────────────────────────
  const addPost = useCallback((post) => {
    setPosts(prev => [post, ...prev]);
    const cacheKey = getCacheKey(spaceId, sort);
    spaceFeedCache.delete(cacheKey);
  }, [spaceId, sort]);

  // ── Optimistic post remove ────────────────────────────────────────────────
  const removePost = useCallback((postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    const cacheKey = getCacheKey(spaceId, sort);
    spaceFeedCache.delete(cacheKey);
  }, [spaceId, sort]);

  // ── Dismiss stale banner ──────────────────────────────────────────────────
  const dismissStaleBanner = useCallback(() => {
    setStaleBanner(false);
    const cacheKey = getCacheKey(spaceId, sort);
    spaceFeedCache.delete(cacheKey);
    fetchFeed(true);
  }, [spaceId, sort, fetchFeed]);

  return {
    posts,
    voteMap,
    bookmarkMap,
    reportMap,
    loading,
    loadingMore,
    hasMore,
    error,
    sort,
    staleBanner,
    loadMore,
    changeSort,
    invalidateCache,
    addPost,
    removePost,
    dismissStaleBanner,
  };
}